// frontend/src/components/sales/SalesLog.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../api';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard, toDate, printSaleReceipt } from '../common/CommonComponents';

// Shared with Orders.js's own copy conceptually, but kept as a separate
// module-level const here rather than a shared import — see
// HANDOFF.md's multi-model rule: Orders.js and SalesLog.js each own
// their WhatsApp menu independently.
const waMenuItemStyle = {
  display: "block", padding: "8px 10px", fontSize: 12.5, borderRadius: 6,
  color: "var(--text)", textDecoration: "none", whiteSpace: "nowrap",
};

function SalesLog({ showAlert, user }) {
  const firstOfMonth = () => { const d=new Date(); return new Date(d.getFullYear(),d.getMonth(),1).toISOString().split("T")[0]; };
  const todayStr     = () => new Date().toISOString().split("T")[0];
 
  const [sales,     setSales]     = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [todayData, setTodayData] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const refundOperationId=useRef(null),refundPayloadSignature=useRef(null),refundSubmitLock=useRef(false);
  const makeRefundOperationId=()=>window.crypto?.randomUUID?.()||`refund-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const [statusF,   setStatusF]   = useState("Completed");
  const [methodF,   setMethodF]   = useState("");
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");
  const [searchQ,   setSearchQ]   = useState("");
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  // Proportional return quantities (Tier 26) — keyed by each sale
  // item's own stable lineId (Codex audit, Medium fix — previously
  // keyed by `${productId}:${sourceType}`, which collapsed two
  // separate lines for the same product into one entry here, silently
  // losing one of them). Defaults to 0 per line (money-only refund
  // unless the manager explicitly enters a quantity for a line).
  const [returnQtys, setReturnQtys] = useState({});
  const [refunding, setRefunding] = useState(false);
  const [whatsappMenuFor, setWhatsappMenuFor] = useState(null);
  const [whatsappLinks, setWhatsappLinks] = useState(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);

  const canRefund = user.role === "manager";

  const toggleWhatsappMenu = async (s) => {
    if (whatsappMenuFor === s.saleId) {
      setWhatsappMenuFor(null);
      setWhatsappLinks(null);
      return;
    }
    setWhatsappMenuFor(s.saleId);
    setWhatsappLinks(null);
    setWhatsappLoading(true);
    try {
      const links = await api.getSaleWhatsappLinks(s.saleId);
      setWhatsappLinks(links);
    } catch(e) {
      showAlert(e.message, "error");
      setWhatsappMenuFor(null);
    }
    setWhatsappLoading(false);
  };
 
  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.getSales({ status: statusF, page, limit: 20, method: methodF, dateFrom, dateTo, search: searchQ }),
      api.getTodaySales(),
    ])
      .then(([r, t]) => { setSales(r.sales); setTotal(r.total); setTodayData(t); })
      .catch(() => showAlert("Failed to load sales", "error"))
      .finally(() => setLoading(false));
  }, [statusF, page, methodF, dateFrom, dateTo, searchQ]);
 
  useEffect(() => { load(); }, [load]);

  const doRefund = async () => {
    if (!refundTarget) return;
    const amt = Number(refundAmount);
    if (!amt || amt <= 0) { showAlert("Enter a refund amount greater than zero", "error"); return; }
    // Proportional returns (Tier 26) — only lines with a quantity > 0
    // entered are sent; this is a partial-quantity operation, not
    // "return everything" or "return nothing."
    const returnedItems = (refundTarget.items || [])
      .map(it => ({ lineId: it.lineId, quantity: Number(returnQtys[it.lineId] || 0) }))
      .filter(r => r.quantity > 0);
    if(refundSubmitLock.current)return;
    refundSubmitLock.current=true;setRefunding(true);
    try {
      const signature=JSON.stringify({saleId:refundTarget.saleId,amount:amt,reason:refundReason||"",returnedItems});
      if(!refundOperationId.current||refundPayloadSignature.current!==signature){refundOperationId.current=makeRefundOperationId();refundPayloadSignature.current=signature;}
      const r = await api.refundSale(refundTarget.saleId, amt, refundReason, returnedItems,refundOperationId.current);
      const returnedCount = (r.returned || []).reduce((s, x) => s + x.quantity, 0);
      // No more partial-success case to report — the refund is now
      // all-or-nothing (Codex re-audit, Critical fix), so reaching here
      // at all means everything requested was applied. A conflict on
      // any line throws instead, caught below like any other error.
      refundOperationId.current=null;refundPayloadSignature.current=null;
      showAlert(`₦${amt.toLocaleString()} refunded${returnedCount ? ` — ${returnedCount} item(s) returned to stock` : ""}`);
      setRefundTarget(null);
      setRefundAmount("");
      setRefundReason("");
      setReturnQtys({});
      load();
    } catch (e) { showAlert(e.message, "error"); }
    finally { refundSubmitLock.current=false;setRefunding(false); }
  };
 
  return (
    <div>
      {refundTarget && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div className="card" style={{ maxWidth: 420, margin: 16 }}>
            <h3 style={{ marginTop: 0 }}>Refund sale {refundTarget.saleId}</h3>
            <p style={{ fontSize: 13, color: "var(--text-2)" }}>
              Refundable up to ₦{fmt(refundTarget.totalAmount - (refundTarget.refundedAmount || 0))} (already refunded: ₦{fmt(refundTarget.refundedAmount || 0)}).
            </p>
            <div className="form-group">
              <label>Amount to refund *</label>
              <input type="number" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} placeholder="0" />
            </div>
            <div className="form-group">
              <label>Reason (optional)</label>
              <input value={refundReason} onChange={e => setRefundReason(e.target.value)} placeholder="e.g. Damaged item" />
            </div>
            {!refundTarget.stockRestoredOnRefund && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 6 }}>
                  Items being returned to stock (optional — leave at 0 for damaged/unsellable goods)
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {(refundTarget.items || []).map(it => {
                    const key = it.lineId;
                    const remaining = it.quantity - (it.returnedQuantity || 0);
                    return (
                      <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5 }}>
                        <span>{it.productName} <span style={{ color: "var(--text-3)" }}>(sold {it.quantity}{it.returnedQuantity ? `, ${it.returnedQuantity} already returned` : ""})</span></span>
                        <input
                          type="number" min="0" max={remaining} disabled={remaining <= 0 || !key}
                          value={returnQtys[key] || ""}
                          onChange={e => setReturnQtys(q => ({ ...q, [key]: Math.min(Number(e.target.value) || 0, remaining) }))}
                          style={{ width: 60, padding: "3px 6px", fontSize: 12 }}
                          placeholder="0"
                          title={key ? undefined : "This line predates per-line return tracking — money-only refund only."}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button className="btn btn-secondary" onClick={() => setRefundTarget(null)} disabled={refunding}>Cancel</button>
              <button className="btn btn-primary" onClick={doRefund} disabled={refunding}>{refunding ? "Refunding…" : "Confirm refund"}</button>
            </div>
          </div>
        </div>
      )}

      {todayData && (
        <div className="card">
          <h2>📅 Today's Sales</h2>
          <div className="stats-grid">
            <StatCard label="Sales Today"      value={todayData.count}/>
            <StatCard label="Revenue Today"    value={fmt(todayData.totalRevenue)} cls="success"/>
            <StatCard label="Profit Today"     value={fmt(todayData.totalProfit)}  cls="success"/>
            <StatCard label="Items Sold Today" value={todayData.totalItems}/>
          </div>
        </div>
      )}
 
      <div className="card">
        <div className="section-header">
          <h2>🛒 All Sales ({total})</h2>
          <button className="btn-sm btn-edit" onClick={api.exportSales}>⬇ CSV</button>
        </div>
 
        {/* Filters */}
        <div className="search-bar" style={{ flexWrap:"wrap", gap:6 }}>
          <input
            placeholder="🔍 Search sale ID, product, ambassador..."
            value={searchQ}
            onChange={e => { setSearchQ(e.target.value); setPage(1); }}
            style={{ flex:2, minWidth:160 }}
          />
          <select value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }}>
            <option value="Completed">Completed</option>
            <option value="Awaiting Payment">Awaiting Payment</option>
            <option value="Cancelled">Cancelled</option>
            <option value="">All Statuses</option>
          </select>
          <select value={methodF} onChange={e => { setMethodF(e.target.value); setPage(1); }}>
            <option value="">All Methods</option>
            <option value="Cash">Cash</option>
            <option value="Transfer">Transfer</option>
            <option value="POS">POS</option>
          </select>
        </div>
 
        {/* Date range filter */}
        <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap" }}>
          <div className="form-group" style={{ flex:1, minWidth:130, margin:0 }}>
            <label style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>From</label>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:13, width:"100%", outline:"none" }}/>
          </div>
          <div className="form-group" style={{ flex:1, minWidth:130, margin:0 }}>
            <label style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>To</label>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
              style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:13, width:"100%", outline:"none" }}/>
          </div>
          {(dateFrom||dateTo||methodF||searchQ) && (
            <div style={{ display:"flex", alignItems:"flex-end" }}>
              <button className="btn-sm btn-secondary" onClick={() => { setDateFrom(""); setDateTo(""); setMethodF(""); setSearchQ(""); setPage(1); }}>
                ✕ Clear
              </button>
            </div>
          )}
        </div>
 
        {loading ? <div className="spinner">Loading...</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Sale ID</th><th>Items</th><th>Gross</th><th>Discount</th>
                  <th>Total</th><th>Profit</th><th>Method</th><th>Status</th>
                  <th>By</th><th>Date</th><th>Print</th>
                </tr>
              </thead>
              <tbody>
                {sales.length===0 && <tr><td colSpan={11} style={{ textAlign:"center",color:"#9ca3af",padding:24 }}>No sales found.</td></tr>}
                {sales.map(s => (
                  <tr key={s.saleId}>
                    <td>{s.saleId}</td>
                    <td style={{ maxWidth:150,overflow:"hidden",textOverflow:"ellipsis" }}>
                      {s.items.map(i=>`${i.productName}×${i.quantity}`).join(", ")}
                    </td>
                    <td>{fmt(s.grossTotal)}</td>
                    <td>{s.discountAmount>0?<span style={{ color:"#e94560" }}>−{fmt(s.discountAmount)}</span>:"—"}</td>
                    <td style={{ fontWeight:700 }}>{fmt(s.totalAmount)}</td>
                    <td style={{ color:"#10b981",fontWeight:600 }}>{fmt(s.profit)}</td>
                    <td>{s.paymentMethod}</td>
                    <td><PayBadge status={s.status}/></td>
                    <td style={{ fontSize:11 }}>{s.loggedBy}</td>
                    <td style={{ fontSize:11 }}>{toDate(s.saleDate)}</td>
                    {/* FIX: Print column was declared in thead but never rendered in tbody */}
                    <td>
                      <div style={{ display: "flex", gap: 4, position: "relative" }}>
                        <button className="btn-sm btn-edit"
                          onClick={() => printSaleReceipt(s, user.businessName, user.businessLogo, user.planFeatures?.hasCustomBranding)}
                          title="Print Receipt">
                          🖨
                        </button>
                        {user.planFeatures?.hasWhatsappTools !== false && (
                          <button className="btn-sm btn-edit" onClick={() => toggleWhatsappMenu(s)} title="Share via WhatsApp">📲</button>
                        )}
                        {whatsappMenuFor === s.saleId && (
                          <div style={{
                            position: "absolute", top: "100%", left: 0, zIndex: 20, marginTop: 4,
                            background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8,
                            boxShadow: "0 4px 16px rgba(0,0,0,.15)", minWidth: 190, padding: 6,
                          }}>
                            {whatsappLoading ? (
                              <div style={{ padding: 10, fontSize: 12, color: "var(--text-3)" }}>Loading...</div>
                            ) : !whatsappLinks?.hasPhone ? (
                              <div style={{ padding: 10, fontSize: 12, color: "var(--text-3)" }}>No phone on file for this client.</div>
                            ) : (
                              <>
                                {whatsappLinks.receiptLink && <a href={whatsappLinks.receiptLink} target="_blank" rel="noreferrer" onClick={() => setWhatsappMenuFor(null)} style={waMenuItemStyle}>🧾 Share Receipt</a>}
                                {whatsappLinks.statusUpdateLink && <a href={whatsappLinks.statusUpdateLink} target="_blank" rel="noreferrer" onClick={() => setWhatsappMenuFor(null)} style={waMenuItemStyle}>📦 Send Status Update</a>}
                              </>
                            )}
                          </div>
                        )}
                        {canRefund && s.status === "Completed" && (s.totalAmount - (s.refundedAmount || 0)) > 0 && (
                          <button className="btn-sm btn-edit" onClick={() => { setRefundTarget(s); setRefundAmount(""); setRefundReason(""); setReturnQtys({}); }}>
                            ↩
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager page={page} total={total} per={20} onChange={setPage}/>
      </div>
    </div>
  );
}

export default SalesLog;